import { useState } from 'react';
import { motion } from 'framer-motion';
import {Database, TrendingUp, MessageSquare, Bot, Shield, Zap, CheckCircle2} from 'lucide-react';
import { Link } from 'react-router-dom';
import { trackCTAClick, trackSectionView, initTimeOnPageTracking } from './utils/trackingHelpers';
import FunnelHeader from './components/FunnelHeader';
import { AnimatedSection } from './components/shared/AnimatedSection';
import { FeatureCard } from './components/shared/FeatureCard';
import { FAQ } from './components/shared/FAQ';
import { ActivationModal } from './components/shared/ActivationModal';
import { SocialProof } from './components/conversion/SocialProof';
import { TrialPopupFlow } from './components/conversion/TrialPopupFlow';
import { WallOfLove } from './components/conversion/WallOfLove';
import { gradients } from './styles/gradients';
import { typography, textColors, fontWeights } from './styles/typography';
import { modernButtons } from './styles/modernButtons';

function App() {
  const [isActivationModalOpen, setIsActivationModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | undefined>(undefined);

  // Initialize time tracking
  useState(() => {
    initTimeOnPageTracking();
  });

  const handlePlanActivation = (planType: 'starter-mensual' | 'enterprise-mensual' | 'enterprise-anual' | null, couponCode?: string) => {
    if (planType === 'starter-mensual') {
      setSelectedPlan('starter-mensual');
    } else if (planType === 'enterprise-mensual') {
      setSelectedPlan('enterprise-mensual');
    } else if (planType === 'enterprise-anual') {
      setSelectedPlan('enterprise-anual');
    } else {
      setSelectedPlan('trial-free');
    }
    
    if (couponCode) {
      sessionStorage.setItem('applied_coupon', couponCode);
    }
    
    setIsActivationModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <FunnelHeader />
      
      {/* HERO SECTION - CLARA Y DIRECTA */}
      <section id="inicio" className={`relative min-h-screen flex items-center justify-center ${gradients.hero} overflow-hidden pt-20`}>
        <div className="relative z-10 max-w-[2400px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col items-center gap-12">
            
            {/* Texto Hero */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-5xl"
            >
              <h1 className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl ${fontWeights.black} text-white leading-tight mb-6`}>
                CADA LEAD QUE TARDAS EN RESPONDER ES DINERO EN EL BOLSILLO DE TU COMPETENCIA
              </h1>
              <p className={`text-xl md:text-2xl text-gray-100 mb-8 leading-relaxed`}>
                Mientras tú "lo piensas", ellos cierran TUS ventas. Esto está afectando cada día tu cuenta bancaria. Esto no es una oferta. Es la única salida si NO quieres pagar el IMPUESTO A LA ESTUPIDEZ.
              </p>
              
              {/* Stats Compactos */}
              <div className="grid grid-cols-3 gap-4 mb-8 max-w-xl mx-auto">
                <div className="text-center">
                  <p className={`text-3xl md:text-4xl ${fontWeights.black} text-[#00C2FF]`}>24/7</p>
                  <p className="text-sm text-gray-300">Disponible</p>
                </div>
                <div className="text-center">
                  <p className={`text-3xl md:text-4xl ${fontWeights.black} text-[#00C2FF]`}>3min</p>
                  <p className="text-sm text-gray-300">Respuesta</p>
                </div>
                <div className="text-center">
                  <p className={`text-3xl md:text-4xl ${fontWeights.black} text-[#00C2FF]`}>72h</p>
                  <p className="text-sm text-gray-300">Setup</p>
                </div>
              </div>

              <button 
                onClick={() => {
                  trackCTAClick('cta-hero', 'Lo Quiero', 'hero');
                  setIsActivationModalOpen(true);
                }}
                className={`${modernButtons.primary} w-full sm:w-auto`}
              >
                Lo Quiero
              </button>
            </motion.div>

            {/* Video - DOBLE DE TAMAÑO */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative w-full max-w-[2000px]"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-[#2A1E42] to-gray-900" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src="https://player.vimeo.com/video/1151200748?badge=0&autopause=0&autoplay=1&muted=0&player_id=0&app_id=58479&controls=1"
                  className="absolute top-0 left-0 w-full h-full"
                  style={{ border: 'none' }}
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                  title="Video Hero ZoeFel IA"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="absolute -bottom-3 -right-3 bg-gradient-to-br from-[#00C2FF] to-[#00A8E8] text-white px-4 py-2 rounded-lg shadow-xl z-10">
                <p className="text-xs font-bold">🎥 Con Audio</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PROBLEMA - IMPACTO DIRECTO */}
      <AnimatedSection id="problema" className="py-24 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onViewportEnter={() => trackSectionView('Problema', 'problema')}
            className="text-center mb-16"
          >
            <h2 className={`text-3xl md:text-5xl ${fontWeights.black} text-gray-900 mb-6`}>
              TU COMPETENCIA TE ESTÁ ROBANDO EN VIVO Y EN DIRECTO
            </h2>
            <p className={`text-xl ${textColors.secondary} max-w-3xl mx-auto`}>
              Ellos responden en segundos. Tú en 2 horas. Ellos cierran mientras duermes. Tú pagas el IMPUESTO A LA ESTUPIDEZ por CADA lead muerto. No es suerte. Es que tienen lo que tú NO tienes.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: '💀', title: 'Tu Excel está muerto', desc: 'Ese archivo con 300 contactos sin responder. Sí, esos leads están vivos... contestando los mensajes de tu competencia ahora mismo.' },
              { icon: '💸', title: 'Impuesto a la estupidez', desc: 'hasta $1,850 USD cada mes que NO automatizas. Sí, cada mes. Mientras lees esto.' },
              { icon: '😂', title: 'Ellos ya ganaron', desc: 'Responden en 3 minutos. Tú todavía buscas ese Excel del mes pasado. ¿Adivina quién cerró la venta?' }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white p-8 rounded-xl border-2 border-red-500/30 hover:border-red-500/60 shadow-lg transition-all"
              >
                <div className="text-5xl mb-4">{item.icon}</div>
                <h3 className={`text-xl ${fontWeights.bold} text-gray-900 mb-2`}>{item.title}</h3>
                <p className={`${typography.bodyBase} ${textColors.secondary}`}>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* ATAQUE DIRECTO A LA COMPETENCIA - SIN PIEDAD */}
      <AnimatedSection id="competencia" className="py-24 px-6 bg-gradient-to-br from-[#2A1E42] to-[#363636]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-gradient-to-br from-[#00C2FF]/10 to-[#2A1E42]/20 border-4 border-[#00C2FF]/30 rounded-3xl p-8 md:p-12 backdrop-blur-sm">
              <div className="text-center mb-10">
                <span className="inline-block px-6 py-2 bg-[#00C2FF] text-white rounded-full font-black mb-4 text-sm uppercase tracking-widest">
                  ⚠️ ADVERTENCIA: LA VERDAD SOBRE TU "INFRAESTRUCTURA"
                </span>
                <h3 className={`text-3xl md:text-4xl ${fontWeights.black} text-white mb-4`}>
                  ¿Todavía Usando Estas Porquerías?
                </h3>
                <p className="text-xl text-gray-200 font-semibold max-w-3xl mx-auto mb-6">
                  La mayoría de los negocios no tienen una infraestructura; tienen un <strong className="text-red-400">monstruo pegado con cinta adhesiva</strong>. Tienes ClickFunnels para las páginas, ActiveCampaign para los correos, Calendly para las citas y HubSpot para el CRM. Y en medio de todo eso, tienes a <strong className="text-[#00C2FF]">Zapier cobrándote una COMISIÓN</strong> cada vez que intentas mover un dato de un lado a otro. <strong className="text-yellow-400">Estás pagando el IMPUESTO A LA ESTUPIDEZ.</strong>
                </p>
                <p className="text-lg text-gray-300 font-semibold max-w-3xl mx-auto">
                  Esto NO es otro software que te abandona con 47 tutoriales de YouTube. Es un <strong className="text-[#00C2FF]">SISTEMA COMPLETO implementado por nosotros</strong>. Como tener un SDR 24/7. Sin vacaciones. Sin dramas. Sin renuncias.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
                {/* HubSpot - El Elefante */}
                <motion.div 
                  whileHover={{ scale: 1.02 }}
                  className="bg-white/95 border-l-4 border-[#2A1E42] rounded-xl p-6 shadow-lg"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="text-4xl">🐘</div>
                    <div className="flex-1">
                      <h4 className={`text-xl ${fontWeights.black} text-[#2A1E42] mb-2`}>
                        HubSpot (El Elefante)
                      </h4>
                      <p className="text-[#2A1E42] font-bold text-sm mb-3">Excelente... castigándote por crecer</p>
                    </div>
                  </div>
                  <p className="text-[#363636] leading-relaxed mb-3">
                    Te cobran MÁS por cada contacto que agregas. Con ZoeFel tienes <strong className="text-[#00C2FF]">Contactos Ilimitados</strong>. No pagas peaje por tu éxito.
                  </p>
                  <p className="text-sm text-[#2A1E42] font-bold italic">
                    ¿Prefieres un socio o un cobrador de impuestos?
                  </p>
                </motion.div>

                {/* ClickFunnels / Leadpages */}
                <motion.div 
                  whileHover={{ scale: 1.02 }}
                  className="bg-white/95 border-l-4 border-[#00C2FF] rounded-xl p-6 shadow-lg"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="text-4xl">🎨</div>
                    <div className="flex-1">
                      <h4 className={`text-xl ${fontWeights.black} text-[#2A1E42] mb-2`}>
                        ClickFunnels / Leadpages
                      </h4>
                      <p className="text-[#00C2FF] font-bold text-sm mb-3">Páginas bonitas, ventas MUERTAS</p>
                    </div>
                  </div>
                  <p className="text-[#363636] leading-relaxed mb-3">
                    Solo hacen páginas bonitas, pero NO gestionan la venta. Son estáticas. Un embudo sin seguimiento es una <strong className="text-[#2A1E42]">página web CARA</strong>.
                  </p>
                  <p className="text-sm text-[#00C2FF] font-bold italic">
                    ZoeFel unifica embudo + CRM + IA. No solo capturas, CIERRAS.
                  </p>
                </motion.div>

                {/* Kajabi / Hotmart */}
                <motion.div 
                  whileHover={{ scale: 1.02 }}
                  className="bg-white/95 border-l-4 border-[#BC9C71] rounded-xl p-6 shadow-lg"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="text-4xl">🪱</div>
                    <div className="flex-1">
                      <h4 className={`text-xl ${fontWeights.black} text-[#2A1E42] mb-2`}>
                        Kajabi / Hotmart
                      </h4>
                      <p className="text-[#BC9C71] font-bold text-sm mb-3">El Socio Parásito</p>
                    </div>
                  </div>
                  <p className="text-[#363636] leading-relaxed mb-3">
                    Se quedan con un % de CADA curso que vendes. Nosotros te cobramos la tecnología, <strong className="text-[#2A1E42]">NO tu esfuerzo</strong>. Con ZoeFel te quedas con el 100% de tu dinero.
                  </p>
                  <p className="text-sm text-[#BC9C71] font-bold italic">
                    Cero comisiones por transacción. Punto.
                  </p>
                </motion.div>

                {/* Leadsales / Whaticket */}
                <motion.div 
                  whileHover={{ scale: 1.02 }}
                  className="bg-white/95 border-l-4 border-[#757575] rounded-xl p-6 shadow-lg"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="text-4xl">🧸</div>
                    <div className="flex-1">
                      <h4 className={`text-xl ${fontWeights.black} text-[#2A1E42] mb-2`}>
                        Leadsales / Whaticket
                      </h4>
                      <p className="text-[#757575] font-bold text-sm mb-3">Juguetes Operativos</p>
                    </div>
                  </div>
                  <p className="text-[#363636] leading-relaxed mb-3">
                    Buenas bandejas para chatear, pero NO son sistemas de venta. Sin funnels, sin email marketing, <strong className="text-[#2A1E42]">sin IA de cierre avanzada</strong>.
                  </p>
                  <p className="text-sm text-[#757575] font-bold italic">
                    ZoeFel es una máquina de facturación COMPLETA.
                  </p>
                </motion.div>
              </div>

              {/* CTA en la sección de competencia */}
              <div className="mt-10 text-center">
                <div className="bg-gradient-to-r from-[#00C2FF]/20 to-[#2A1E42]/20 border-2 border-[#00C2FF] rounded-2xl p-6 max-w-3xl mx-auto backdrop-blur-sm">
                  <p className={`text-2xl ${fontWeights.black} text-white mb-2`}>
                    ¿Cuánto tiempo más vas a pagar el IMPUESTO A LA ESTUPIDEZ?
                  </p>
                  <p className="text-gray-200 mb-4">
                    Cada mes sin ZoeFel IA hasta $1,850 USD tirados a la basura en herramientas que NO funcionan juntas
                  </p>
                  <button 
                    onClick={() => {
                      trackCTAClick('cta-competencia', 'Lo Quiero', 'competencia');
                      setIsActivationModalOpen(true);
                    }}
                    className={`${modernButtons.primary}`}
                  >
                    Lo Quiero
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* SOLUCIÓN - VALOR CLARO */}
      <AnimatedSection id="solucion" className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-[#00C2FF]/20 text-[#00C2FF] rounded-full font-bold mb-4">
              LA SOLUCIÓN
            </span>
            <h2 className={`text-3xl md:text-5xl ${fontWeights.black} text-gray-900 mb-6`}>
              Te presentamos la Plataforma de Venta Autónoma ZoeFel IA.
            </h2>
            <p className={`text-xl ${textColors.secondary} max-w-3xl mx-auto mb-8`}>
              Es una <strong className="text-gray-900">Solución IA todo en uno completamente implementada, configurada y gestionada por nosotros</strong>. No vendemos herramientas, entregamos resultados. <strong className="text-gray-900">Atención garantizada 24/7 desde que el cliente muestra interés hasta que paga.</strong> Lo integramos en tu negocio para que funcione como un miembro más de tu equipo: un <strong className="text-gray-900">SDR (Representante de Desarrollo de Ventas) que nunca duerme.</strong>
            </p>
          </div>

          {/* 5 Motores de Crecimiento */}
          <div className="bg-[#231936] rounded-3xl p-8 md:p-12 mb-16">
            <div className="text-center mb-12">
              <span className="text-[#00C2FF] font-bold tracking-[4px] text-sm uppercase mb-2 block">18 Procesos Autónomos</span>
              <h3 className={`text-3xl md:text-4xl text-white mb-4 ${fontWeights.black}`}>5 Motores de Crecimiento</h3>
              <p className="text-gray-400 max-w-2xl mx-auto font-medium">Un sistema que trabaja por ti 24/7: Desde que el cliente muestra interés hasta que paga.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Motor 1: Adquisición */}
              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="bg-[rgba(255,255,255,0.03)] backdrop-blur-xl p-8 rounded-3xl border border-white/10 hover:border-cyan-400/50 transition-all duration-300 shadow-2xl h-full flex flex-col"
              >
                <div className="flex justify-center mb-6">
                  <i className="fa-solid fa-bullseye text-5xl text-cyan-400 drop-shadow-[0_0_15px_rgba(0,194,255,0.5)]"></i>
                </div>
                <h4 className="text-xl text-white text-center mb-4 font-bold">Adquisición</h4>
                <p className="text-sm text-gray-200 mb-6 text-center">Filtra curiosos y detecta dinero.</p>
                <ul className="space-y-3 text-xs text-gray-200 pt-4 border-t border-white/10 flex-grow font-medium">
                  <li className="flex gap-2"><i className="fa-solid fa-check text-cyan-400"></i> Respuesta Inmediata 24/7</li>
                  <li className="flex gap-2"><i className="fa-solid fa-check text-cyan-400"></i> Detector de Interés en Precios</li>
                  <li className="flex gap-2"><i className="fa-solid fa-check text-cyan-400"></i> Contacto Proactivo Automático</li>
                  <li className="flex gap-2"><i className="fa-solid fa-check text-cyan-400"></i> Alerta Inmediata Cliente VIP</li>
                </ul>
              </motion.div>

              {/* Motor 2: Conversión */}
              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="bg-[rgba(255,255,255,0.03)] backdrop-blur-xl p-8 rounded-3xl border border-white/10 hover:border-green-400/50 transition-all duration-300 shadow-2xl h-full flex flex-col"
              >
                <div className="flex justify-center mb-6">
                  <i className="fa-solid fa-money-bill-trend-up text-5xl text-green-400 drop-shadow-[0_0_15px_rgba(74,222,128,0.5)]"></i>
                </div>
                <h4 className="text-xl text-white text-center mb-4 font-bold">Conversión</h4>
                <p className="text-sm text-gray-200 mb-6 text-center">Ataca la indecisión con lógica y urgencia.</p>
                <ul className="space-y-3 text-xs text-gray-200 pt-4 border-t border-white/10 flex-grow font-medium">
                  <li className="flex gap-2"><i className="fa-solid fa-check text-green-400"></i> Clasificación Automática de Clientes</li>
                  <li className="flex gap-2"><i className="fa-solid fa-check text-green-400"></i> Pitch de Venta Contextual</li>
                  <li className="flex gap-2"><i className="fa-solid fa-check text-green-400"></i> Recuperación de Carrito Abandonado</li>
                  <li className="flex gap-2"><i className="fa-solid fa-check text-green-400"></i> Manejo de Silencios</li>
                  <li className="flex gap-2"><i className="fa-solid fa-check text-green-400"></i> Salvavidas: Downsell a Prueba</li>
                  <li className="flex gap-2"><i className="fa-solid fa-check text-green-400"></i> Agendamiento de Citas por Voz</li>
                  <li className="flex gap-2"><i className="fa-solid fa-check text-green-400"></i> Gestión de Agenda y Recordatorios</li>
                </ul>
              </motion.div>

              {/* Motor 3: Retención */}
              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="bg-[rgba(255,255,255,0.03)] backdrop-blur-xl p-8 rounded-3xl border border-white/10 hover:border-purple-400/50 transition-all duration-300 shadow-2xl h-full flex flex-col"
              >
                <div className="flex justify-center mb-6">
                  <i className="fa-solid fa-shield-halved text-5xl text-purple-400 drop-shadow-[0_0_15px_rgba(192,132,252,0.5)]"></i>
                </div>
                <h4 className="text-xl text-white text-center mb-4 font-bold">Retención</h4>
                <p className="text-sm text-gray-200 mb-6 text-center">Cobra sin perseguir y asegura al cliente.</p>
                <ul className="space-y-3 text-xs text-gray-200 pt-4 border-t border-white/10 flex-grow font-medium">
                  <li className="flex gap-2"><i className="fa-solid fa-check text-purple-400"></i> Bienvenida y Acceso Inmediato</li>
                  <li className="flex gap-2"><i className="fa-solid fa-check text-purple-400"></i> Cobranza: Aviso Amable</li>
                  <li className="flex gap-2"><i className="fa-solid fa-check text-purple-400"></i> Cobranza: Ultimátum de Servicio</li>
                  <li className="flex gap-2"><i className="fa-solid fa-check text-purple-400"></i> Conversión de Prueba a Pago</li>
                  <li className="flex gap-2"><i className="fa-solid fa-check text-purple-400"></i> No dejar que se enfríen</li>
                </ul>
              </motion.div>

              {/* Motor 4: Defensa */}
              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="bg-[rgba(255,255,255,0.03)] backdrop-blur-xl p-8 rounded-3xl border border-white/10 hover:border-red-400/50 transition-all duration-300 shadow-2xl h-full flex flex-col"
              >
                <div className="flex justify-center mb-6">
                  <i className="fa-solid fa-user-shield text-5xl text-red-400 drop-shadow-[0_0_15px_rgba(248,113,113,0.5)]"></i>
                </div>
                <h4 className="text-xl text-white text-center mb-4 font-bold">Defensa</h4>
                <p className="text-sm text-gray-200 mb-6 text-center">Protege ingresos y revive clientes.</p>
                <ul className="space-y-3 text-xs text-gray-200 pt-4 border-t border-white/10 flex-grow font-medium">
                  <li className="flex gap-2"><i className="fa-solid fa-check text-red-400"></i> Reactivación de Clientes Inactivos</li>
                  <li className="flex gap-2"><i className="fa-solid fa-check text-red-400"></i> Filtro Técnico de Soporte</li>
                  <li className="flex gap-2"><i className="fa-solid fa-check text-red-400"></i> Ofertas de Upsell Automáticas</li>
                  <li className="flex gap-2"><i className="fa-solid fa-check text-red-400"></i> Gestión de Tickets de Soporte</li>
                  <li className="flex gap-2"><i className="fa-solid fa-check text-red-400"></i> Mantener vivo el interés</li>
                </ul>
              </motion.div>
            </div>

            {/* Motor Central (Core) */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-[rgba(255,255,255,0.03)] backdrop-blur-xl p-10 rounded-3xl border border-cyan-400/30 relative overflow-hidden shadow-2xl"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-900/10 to-transparent pointer-events-none"></div>
              <div className="relative z-10 flex flex-col md:flex-row items-center gap-10">
                <div className="shrink-0 text-center">
                  <div className="w-24 h-24 bg-cyan-500/10 rounded-2xl flex items-center justify-center text-cyan-400 text-5xl shadow-[0_0_30px_rgba(0,194,255,0.3)] border border-cyan-500/20 relative mb-4 mx-auto">
                    <i className="fa-solid fa-microchip"></i>
                    <div className="absolute -inset-4 bg-cyan-500/5 blur-xl rounded-full -z-10"></div>
                  </div>
                  <span className="text-xs font-bold text-cyan-400 tracking-widest uppercase">Motor Central</span>
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h4 className={`text-3xl ${fontWeights.bold} text-white mb-4`}>Plataforma de Venta Autónoma</h4>
                  <p className="text-gray-100 text-lg mb-8 font-light">
                    <strong className="text-[#00C2FF]">Atención garantizada 24/7 desde que el cliente muestra interés hasta que paga.</strong> Agentes Autónomos que ejecutan 18 procesos, 1 flujo de venta, 1 de servicio. Contestan en 5 segundos. Atienden a 1,000 personas simultáneamente sin pedir aumento, sin dramas y sin renunciar el lunes.
                  </p>
                  <div className="flex flex-wrap justify-center md:justify-start gap-4">
                    <span className="bg-gradient-to-r from-[#BC9C71] to-[#D4AF37] text-[#2A1E42] font-bold px-4 py-1.5 rounded-full text-xs uppercase shadow-lg">Calificar</span>
                    <span className="bg-gradient-to-r from-[#BC9C71] to-[#D4AF37] text-[#2A1E42] font-bold px-4 py-1.5 rounded-full text-xs uppercase shadow-lg">Dar Seguimiento</span>
                    <span className="bg-gradient-to-r from-[#BC9C71] to-[#D4AF37] text-[#2A1E42] font-bold px-4 py-1.5 rounded-full text-xs uppercase shadow-lg">Agendar</span>
                    <span className="bg-gradient-to-r from-[#BC9C71] to-[#D4AF37] text-[#2A1E42] font-bold px-4 py-1.5 rounded-full text-xs uppercase shadow-lg">Vender</span>
                    <span className="bg-gradient-to-r from-[#BC9C71] to-[#D4AF37] text-[#2A1E42] font-bold px-4 py-1.5 rounded-full text-xs uppercase shadow-lg">Atender</span>
                  </div>
                </div>
                <div className="shrink-0">
                  <button 
                    onClick={() => {
                      trackCTAClick('cta-motores', 'Ver Detalles', 'motores');
                      setIsActivationModalOpen(true);
                    }}
                    className="w-16 h-16 rounded-full p-0 flex items-center justify-center text-2xl bg-gradient-to-r from-[#00C2FF] to-[#00A8E8] text-white shadow-[0_6px_0_rgba(0,168,232,0.8),0_10px_25px_rgba(0,194,255,0.4)] hover:shadow-[0_8px_0_rgba(0,168,232,0.8),0_15px_35px_rgba(0,194,255,0.5)] hover:-translate-y-1 active:translate-y-0.5 transition-all duration-200"
                  >
                    <i className="fa-solid fa-arrow-down"></i>
                  </button>
                </div>
              </div>
            </motion.div>
          </div>


        </div>
      </AnimatedSection>

      {/* OFERTA - PRICING SECTION */}
      <AnimatedSection id="pricing" className="py-24 px-6 bg-[#2A1E42]">
        <div className="max-w-6xl mx-auto">
          {/* Banner de Cupón Disponible */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8 max-w-3xl mx-auto"
          >
            <div className="bg-gradient-to-r from-yellow-500/20 via-orange-500/20 to-yellow-500/20 border-2 border-yellow-400/50 rounded-2xl p-4 text-center backdrop-blur-sm">
              <p className="text-yellow-300 font-bold text-sm mb-1">🎁 CUPÓN REQUERIDO PARA PRECIO DE LANZAMIENTO</p>
              <p className="text-white text-lg font-black">50% OFF aplicado con cupón (precios ya incluyen descuento)</p>
              <p className="text-gray-300 text-xs mt-2">El cupón aparecerá mientras navegas • Sin cupón: precios regulares aplican</p>
            </div>
          </motion.div>

          <div className="text-center mb-16">
            <h2 className={`text-4xl md:text-5xl text-white mb-4 ${fontWeights.black}`}>Oferta Única de Lanzamiento</h2>
            <p className="text-gray-400 text-lg font-light">50% OFF con cupón exclusivo. Precios mostrados ya incluyen el descuento.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto items-center">
            
            {/* Monthly Plan */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-[rgba(255,255,255,0.03)] backdrop-blur-xl p-8 rounded-3xl border border-white/10 flex flex-col h-full relative shadow-2xl"
            >
              <div className="mb-6 border-b border-white/10 pb-6 text-center">
                <h3 className="text-2xl font-bold text-white">Enterprise Mensual</h3>
                <p className="text-cyan-400 text-sm mt-1 font-semibold">Flexibilidad y Potencia</p>
              </div>
              
              <div className="text-center mb-8">
                <div className="mb-3">
                  <span className="text-5xl font-extrabold text-white block mb-2">$170</span>
                  <div className="text-sm text-gray-400 font-medium">
                    USD / mes <span className="line-through text-red-400 ml-2 font-bold">$320 USD</span>
                  </div>
                </div>
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2 inline-block">
                  <p className="text-green-300 text-xs font-bold">✅ Precio con cupón aplicado (50% OFF)</p>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-6 border border-white/5 mb-8 flex-grow">
                <p className="text-xs font-bold text-gray-400 uppercase mb-4 tracking-widest text-center">INCLUYE:</p>
                <ul className="space-y-4 text-sm text-gray-200 font-medium">
                  <li className="flex items-center gap-3"><i className="fa-solid fa-users text-cyan-400"></i> Usuarios Ilimitados</li>
                  <li className="flex items-center gap-3"><i className="fa-solid fa-address-book text-cyan-400"></i> Clientes Ilimitados</li>
                  <li className="flex items-center gap-3"><i className="fa-solid fa-robot text-purple-400"></i> <strong>2 Licencias IA</strong> (Uso Ilimitado)</li>
                  <li className="flex items-center gap-3"><i className="fa-solid fa-gift text-green-400"></i> Bono: <strong>Mes 2 GRATIS</strong></li>
                </ul>
                
                <div className="mt-6 pt-4 border-t border-white/10">
                  <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">SOLUCIONES INTEGRADAS (TODO EN UNO):</p>
                  <div className="text-xs text-gray-400 grid grid-cols-2 gap-2 font-medium">
                    <span><i className="fa-solid fa-check text-cyan-400 mr-1"></i> Plataforma de Ventas Autónoma</span>
                    <span><i className="fa-solid fa-check text-cyan-400 mr-1"></i> CRM & Pipelines</span>
                    <span><i className="fa-solid fa-check text-cyan-400 mr-1"></i> Plataforma de Cursos</span>
                    <span><i className="fa-solid fa-check text-cyan-400 mr-1"></i> Agendamiento (Calendario)</span>
                    <span><i className="fa-solid fa-check text-cyan-400 mr-1"></i> Embudos & Sitios Web</span>
                    <span><i className="fa-solid fa-check text-cyan-400 mr-1"></i> Email Marketing & SMS</span>
                    <span><i className="fa-solid fa-check text-cyan-400 mr-1"></i> Automatización (Workflows)</span>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => {
                  trackCTAClick('cta-pricing-mensual', 'Enterprise Mensual', 'pricing');
                  handlePlanActivation('enterprise-mensual');
                }}
                className="w-full bg-gradient-to-r from-[#00C2FF] to-[#00A8E8] text-white font-extrabold uppercase tracking-[2px] rounded-xl shadow-[0_6px_0_rgba(0,168,232,0.8),0_10px_25px_rgba(0,194,255,0.4)] hover:shadow-[0_8px_0_rgba(0,168,232,0.8),0_15px_35px_rgba(0,194,255,0.5)] hover:-translate-y-1 active:translate-y-0.5 transition-all duration-200 py-3"
              >
                Lo quiero
              </button>
              <p className="text-[10px] text-gray-400 text-center mt-3">🎟️ Cupón requerido en proceso de activación</p>
              <p className="text-[10px] text-gray-500 text-center mt-1">IA adicional pago por consumo.</p>
            </motion.div>

            {/* Annual Plan (Highlighted) */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-[#181126] p-10 rounded-3xl border-2 border-[#BC9C71] relative shadow-2xl transform md:scale-105 z-10 flex flex-col h-full"
            >
              <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#BC9C71] to-[#D4AF37] text-[#2A1E42] px-8 py-2 rounded-full text-xs font-black uppercase tracking-widest shadow-lg whitespace-nowrap">
                Mejor Valor (Ahorra + VIP)
              </div>

              <div className="mb-8 border-b border-white/10 pb-6 text-center mt-6">
                <h3 className="text-3xl font-bold text-[#BC9C71]">Enterprise Anual</h3>
                <p className="text-gray-300 text-sm mt-1 font-medium">Dominio Total y Soporte VIP</p>
              </div>
              
              <div className="text-center mb-8">
                <div className="mb-3">
                  <span className="text-6xl font-extrabold text-white block mb-2">$1,920</span>
                  <div className="text-sm text-gray-400 font-medium">
                    USD / año <span className="text-green-400 font-bold ml-2">Equiv. $160/mes</span>
                    <div className="line-through text-red-400 text-xs mt-1">$3,840 USD</div>
                  </div>
                </div>
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2 inline-block">
                  <p className="text-green-300 text-xs font-bold">✅ Precio con cupón aplicado (50% OFF)</p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-indigo-900/40 to-purple-900/40 rounded-xl p-6 border border-indigo-500/30 mb-8 flex-grow">
                <div className="mb-4 pb-4 border-b border-white/10 opacity-75">
                  <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">BASE (IGUAL QUE MENSUAL):</p>
                  <ul className="space-y-1 text-xs text-gray-300 font-medium">
                    <li className="flex items-center gap-2"><i className="fa-solid fa-check text-cyan-400"></i> Usuarios y Clientes Ilimitados</li>
                    <li className="flex items-center gap-2"><i className="fa-solid fa-check text-cyan-400"></i> Todas las Soluciones Integradas</li>
                  </ul>
                </div>

                <p className="text-xs font-bold text-cyan-400 uppercase mb-4 tracking-widest text-center">MEJORAS EXCLUSIVAS ANUAL:</p>
                <ul className="space-y-4 text-sm text-white font-bold">
                  <li className="flex items-center gap-3"><i className="fa-solid fa-infinity text-[#BC9C71] text-lg"></i> <span><strong>TODAS las Licencias IA Ilimitadas</strong><br /><span className="text-[10px] text-gray-400 font-normal">Sin restricciones de uso.</span></span></li>
                  <li className="flex items-center gap-3"><i className="fa-solid fa-crown text-[#BC9C71] text-lg"></i> <strong>Soporte Prioritario VIP</strong></li>
                  <li className="flex items-center gap-3"><i className="fa-solid fa-handshake text-[#BC9C71] text-lg"></i> <strong>Onboarding Personalizado 1 a 1</strong></li>
                  <li className="flex items-center gap-3"><i className="fa-solid fa-gift text-[#BC9C71] text-lg"></i> Bono: <strong>Mes 13 GRATIS</strong></li>
                </ul>
              </div>

              <button 
                onClick={() => {
                  trackCTAClick('cta-pricing-anual', 'Enterprise Anual', 'pricing');
                  handlePlanActivation('enterprise-anual');
                }}
                className="w-full text-lg py-5 bg-gradient-to-r from-[#BC9C71] to-[#a38258] text-[#2A1E42] font-extrabold uppercase tracking-[2px] rounded-xl shadow-[0_6px_0_rgba(188,156,113,0.8),0_10px_25px_rgba(188,156,113,0.4)] hover:shadow-[0_8px_0_rgba(188,156,113,0.8),0_15px_35px_rgba(188,156,113,0.5)] hover:-translate-y-1 active:translate-y-0.5 transition-all duration-200"
              >
                Lo quiero
              </button>
              <p className="text-[10px] text-gray-400 text-center mt-3">🎟️ Cupón requerido en proceso de activación</p>
            </motion.div>
          </div>
        </div>
      </AnimatedSection>

      {/* COMPARACIÓN DE COSTOS ANUALES */}
      <AnimatedSection className="py-24 px-6 bg-[#2A1E42] border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-3xl font-bold mb-6 text-center text-white">El IMPUESTO A LA ESTUPIDEZ: Lo Que Pagas por NO Tener Esto</h3>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[rgba(255,255,255,0.03)] backdrop-blur-xl p-8 rounded-3xl border border-white/10 shadow-2xl"
          >
            <div className="space-y-8">
              <div>
                <div className="flex justify-between text-sm mb-2 font-bold text-gray-300">
                  <span className="text-red-400">Frankenstein de Apps (Salesforce, etc.)</span>
                  <span>$22,200 USD</span>
                </div>
                <div className="w-full bg-gray-800 rounded-full h-4 overflow-hidden">
                  <div className="bg-red-500 h-4 rounded-full" style={{width: '100%'}}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-2 font-bold text-gray-300">
                  <span className="text-yellow-400">Empleado Humano (Salario + Cargas)</span>
                  <span>$14,400 USD</span>
                </div>
                <div className="w-full bg-gray-800 rounded-full h-4 overflow-hidden">
                  <div className="bg-yellow-500 h-4 rounded-full" style={{width: '65%'}}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-2 font-bold text-white">
                  <span className="text-cyan-400">ZoeFel IA™ Enterprise</span>
                  <span className="text-green-400">$1,920 USD</span>
                </div>
                <div className="w-full bg-gray-800 rounded-full h-8 overflow-hidden shadow-[0_0_15px_rgba(0,194,255,0.4)]">
                  <div className="bg-gradient-to-r from-cyan-500 to-blue-600 h-8 rounded-full relative" style={{width: '9%'}}></div>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-white/10 text-center">
              <p className="text-gray-200 text-xs uppercase tracking-widest mb-2 font-bold">Ahorro Anual Proyectado</p>
              <p className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-cyan-400">
                $20,160 USD
              </p>
            </div>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* FUNCIONALIDADES E INSUMOS - TABLAS COMPLETAS */}
      <AnimatedSection id="funcionalidades" className="py-24 px-6 bg-[#231936]">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-3xl font-bold mb-10 text-center text-white">Especificaciones Técnicas</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            
            {/* Features Table */}
            <div className="bg-[rgba(255,255,255,0.05)] backdrop-blur-xl rounded-3xl border border-white/20 overflow-hidden shadow-2xl">
              <div className="p-4 bg-cyan-500/10 border-b border-cyan-400/30 font-bold text-cyan-300 text-center uppercase tracking-wider text-sm">
                Funcionalidades Incluidas (Plan Enterprise)
              </div>
              <div className="h-96 overflow-y-auto p-2" style={{scrollbarWidth: 'thin', scrollbarColor: '#00C2FF #2A1E42'}}>
                <table className="w-full text-left border-collapse">
                  <tbody className="text-xs font-medium">
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 font-bold text-white"><i className="fa-solid fa-rocket text-cyan-400 mr-2"></i>1. Plataforma de Venta Autónoma</td><td className="p-3 text-right text-green-300 font-bold">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 font-bold text-white"><i className="fa-solid fa-robot text-[#BC9C71] mr-2"></i>2. Agentes IA (Mila & Alex)</td><td className="p-3 text-right text-[#BC9C71] font-bold">ILIMITADO*</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-filter text-cyan-400 mr-2"></i>3. Embudos de Venta</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-database text-cyan-400 mr-2"></i>4. CRM & Base de Datos</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-calendar-check text-cyan-400 mr-2"></i>5. Calendario Inteligente</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-file-invoice-dollar text-cyan-400 mr-2"></i>6. Facturación Automática</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-graduation-cap text-cyan-400 mr-2"></i>7. Academia de Cursos</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-handshake text-cyan-400 mr-2"></i>8. Sistema de Afiliados</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-gears text-cyan-400 mr-2"></i>9. Automatización Avanzada</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-brands fa-google text-yellow-400 mr-2"></i>10. Reputación Google</td><td className="p-3 text-right text-yellow-300">Add-on ($20)</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-inbox text-cyan-400 mr-2"></i>11. Bandeja Unificada</td><td className="p-3 text-right text-yellow-300">Variable</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-comment-dots text-cyan-400 mr-2"></i>12. Chat GMB</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-brands fa-facebook-messenger text-blue-400 mr-2"></i>13. Chat FB Messenger</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-message text-cyan-400 mr-2"></i>14. Web Widget</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-phone-slash text-cyan-400 mr-2"></i>15. SMS Llamada Perdida</td><td className="p-3 text-right text-yellow-300">Variable</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-credit-card text-cyan-400 mr-2"></i>16. Texto para Pagar</td><td className="p-3 text-right text-yellow-300">Variable</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-rocket text-cyan-400 mr-2"></i>17. Launchpad</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-share-nodes text-cyan-400 mr-2"></i>18. Planificador Social</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-trophy text-cyan-400 mr-2"></i>19. Oportunidades</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-envelope text-cyan-400 mr-2"></i>20. Email Marketing</td><td className="p-3 text-right text-yellow-300">Variable</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-file-lines text-cyan-400 mr-2"></i>21. Formularios</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-square-poll-vertical text-cyan-400 mr-2"></i>22. Encuestas</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-link text-cyan-400 mr-2"></i>23. Trigger Links</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-table-cells-large text-cyan-400 mr-2"></i>24. Plantillas</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-globe text-cyan-400 mr-2"></i>25. Sitios Web</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-repeat text-cyan-400 mr-2"></i>26. Suscripciones</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-chart-line text-cyan-400 mr-2"></i>27. Informes</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-bolt text-cyan-400 mr-2"></i>28. Activadores</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-bullhorn text-cyan-400 mr-2"></i>29. Campañas</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-blog text-cyan-400 mr-2"></i>30. Blogs</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-folder text-cyan-400 mr-2"></i>31. Documentos</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-certificate text-cyan-400 mr-2"></i>32. Certificados</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-map-pin text-cyan-400 mr-2"></i>33. Seguimiento GMB</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-users text-cyan-400 mr-2"></i>34. Gokollab</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-clipboard-question text-cyan-400 mr-2"></i>35. Cuestionarios</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-qrcode text-cyan-400 mr-2"></i>36. Códigos QR</td><td className="p-3 text-right text-gray-300">Incluido</td></tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]"><td className="p-3 text-gray-200"><i className="fa-solid fa-tag text-cyan-400 mr-2"></i>37. Factura White Label</td><td className="p-3 text-right text-yellow-300">Add-on ($100)</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Inputs Table (Tarifas) */}
            <div className="bg-[rgba(255,255,255,0.05)] backdrop-blur-xl rounded-3xl border border-[#BC9C71]/30 overflow-hidden shadow-2xl">
              <div className="p-4 bg-[#BC9C71]/10 border-b border-[#BC9C71]/30 font-bold text-[#D4AF37] text-center uppercase tracking-wider text-sm">
                Tabla de Insumos (Tarifas Preferenciales)
              </div>
              <div className="h-96 overflow-y-auto p-2" style={{scrollbarWidth: 'thin', scrollbarColor: '#00C2FF #2A1E42'}}>
                <table className="w-full text-left border-collapse">
                  <thead className="text-xs uppercase text-gray-300 bg-black/30 sticky top-0">
                    <tr><th className="p-2"><i className="fa-solid fa-tag text-[#BC9C71] mr-2"></i>Servicio</th><th className="p-2 text-right">Costo</th><th className="p-2 text-right">x $10 USD</th></tr>
                  </thead>
                  <tbody className="text-xs font-medium">
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]">
                      <td className="p-3 text-gray-200"><i className="fa-solid fa-phone-volume text-green-400 mr-2"></i>Llamadas Salientes</td>
                      <td className="p-3 text-right text-gray-300">$0.028/min</td>
                      <td className="p-3 text-right text-cyan-300">357 min</td>
                    </tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]">
                      <td className="p-3 text-gray-200"><i className="fa-solid fa-phone-flip text-blue-400 mr-2"></i>Llamadas Entrantes</td>
                      <td className="p-3 text-right text-gray-300">$0.017/min</td>
                      <td className="p-3 text-right text-cyan-300">588 min</td>
                    </tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]">
                      <td className="p-3 text-gray-200"><i className="fa-solid fa-comment-sms text-purple-400 mr-2"></i>SMS</td>
                      <td className="p-3 text-right text-gray-300">$0.017/seg</td>
                      <td className="p-3 text-right text-cyan-300">602 msj</td>
                    </tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]">
                      <td className="p-3 text-gray-200"><i className="fa-solid fa-envelope text-red-400 mr-2"></i>Email</td>
                      <td className="p-3 text-right text-gray-300">$0.0014/envío</td>
                      <td className="p-3 text-right text-cyan-300">7,142 emails</td>
                    </tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]">
                      <td className="p-3 text-gray-200"><i className="fa-solid fa-envelope-circle-check text-green-400 mr-2"></i>Verif. Email</td>
                      <td className="p-3 text-right text-gray-300">$0.005/verif</td>
                      <td className="p-3 text-right text-cyan-300">2,000 verif</td>
                    </tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]">
                      <td className="p-3 text-gray-200"><i className="fa-solid fa-robot text-cyan-400 mr-2"></i>Automatización</td>
                      <td className="p-3 text-right text-gray-300">$0.020/ejec</td>
                      <td className="p-3 text-right text-cyan-300">500 ejec</td>
                    </tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]">
                      <td className="p-3 text-gray-200"><i className="fa-brands fa-whatsapp text-green-400 mr-2"></i>WhatsApp</td>
                      <td className="p-3 text-right text-gray-300">$0.148/conv</td>
                      <td className="p-3 text-right text-cyan-300">67 conv</td>
                    </tr>
                    
                    <tr className="bg-cyan-900/30 border-l-4 border-cyan-400">
                      <td className="p-3 text-white font-bold"><i className="fa-solid fa-brain text-[#BC9C71] mr-2"></i>Agente IA (Chat/Voz)</td>
                      <td className="p-3 text-right text-gray-300 line-through">$0.040</td>
                      <td className="p-3 text-right text-[#D4AF37] font-bold">ILIMITADO*</td>
                    </tr>
                    
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]">
                      <td className="p-3 text-gray-200"><i className="fa-solid fa-pen-fancy text-purple-400 mr-2"></i>Redactor IA (Pago por Uso)</td>
                      <td className="p-3 text-right text-gray-300">$0.180/1k</td>
                      <td className="p-3 text-right text-yellow-300">Pago x Uso</td>
                    </tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]">
                      <td className="p-3 text-gray-200"><i className="fa-solid fa-dumbbell text-orange-400 mr-2"></i>Entrenador IA (Pago por Uso)</td>
                      <td className="p-3 text-right text-gray-300">$0.500/750k</td>
                      <td className="p-3 text-right text-yellow-300">Pago x Uso</td>
                    </tr>
                    <tr className="border-b border-white/10 hover:bg-[rgba(0,194,255,0.08)]">
                      <td className="p-3 text-gray-200"><i className="fa-solid fa-wand-magic-sparkles text-pink-400 mr-2"></i>Funnel AI</td>
                      <td className="p-3 text-right text-gray-300">$1.98/funnel</td>
                      <td className="p-3 text-right text-yellow-300">Pago x Uso</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          
          <p className="text-center text-gray-100 text-xs mt-8 max-w-2xl mx-auto font-medium bg-white/5 rounded-xl p-4 border border-white/10">
            <i className="fa-solid fa-circle-info text-cyan-400 mr-2"></i>*El Plan Enterprise Anual incluye TODAS las licencias IA sin costo de ejecución. El Plan Mensual incluye 2 licencias sin costo de ejecución. El uso de herramientas IA generativas adicionales (Redactor, Entrenador) se cobra por consumo según la tabla.
          </p>
        </div>
      </AnimatedSection>

      {/* CARACTERÍSTICAS - GRID COMPACTO */}
      <AnimatedSection id="caracteristicas" className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className={`text-3xl md:text-5xl ${fontWeights.black} text-gray-900 mb-6`}>
              Todo lo que Necesitas en Un Solo Lugar
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <FeatureCard
              icon={<Database className="w-8 h-8 text-white" />}
              title="CRM Unificado"
              description="Leads organizados. Listos para convertir. Sin Excel del infierno."
              gradient={gradients.primary}
            />
            <FeatureCard
              icon={<TrendingUp className="w-8 h-8 text-white" />}
              title="Pipeline Visual"
              description="Sabes exactamente quién cierra HOY. No adivinas."
            />
            <FeatureCard
              icon={<MessageSquare className="w-8 h-8 text-white" />}
              title="Follow-Up Automático"
              description="WhatsApp, Email, SMS. Mientras duermes, el sistema vende."
            />
            <FeatureCard
              icon={<Bot className="w-8 h-8 text-white" />}
              title="IA 24/7"
              description="Responde a las 3 AM. Califica. Agenda. Cierra."
            />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onViewportEnter={() => trackSectionView('Características', 'caracteristicas')}
            className="text-center mt-12"
          >
            <button 
              onClick={() => {
                trackCTAClick('cta-caracteristicas', 'Lo Quiero', 'caracteristicas');
                setIsActivationModalOpen(true);
              }}
              className={`${modernButtons.primary}`}
            >
              Lo Quiero
            </button>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* GARANTÍA - TRUST BUILDER */}
      <AnimatedSection id="garantia" className="py-24 px-6 bg-gradient-to-br from-[#2A1E42] to-gray-900">
        <div className="max-w-4xl mx-auto text-center text-white">
          <Shield className="w-16 h-16 mx-auto mb-6 text-[#00C2FF]" />
          <h2 className={`text-3xl md:text-5xl ${fontWeights.black} mb-6`}>
            Garantía de 30 Días
          </h2>
          <p className="text-xl text-gray-100 mb-8">
            Si en 30 días no ves más leads calificados, menos tiempo manual y más citas agendadas, trabajamos GRATIS hasta lograrlo.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <p className={`text-3xl ${fontWeights.black} text-[#00C2FF] mb-2`}>+40%</p>
              <p className="text-sm">Leads capturados</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <p className={`text-3xl ${fontWeights.black} text-[#00C2FF] mb-2`}>-60%</p>
              <p className="text-sm">Tiempo manual</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <p className={`text-3xl ${fontWeights.black} text-[#00C2FF] mb-2`}>+25%</p>
              <p className="text-sm">Citas agendadas</p>
            </div>
          </div>

          <button 
            onClick={() => {
              trackCTAClick('cta-garantia', 'Lo Quiero', 'garantia');
              setIsActivationModalOpen(true);
            }}
            className={`${modernButtons.secondary}`}
          >
            Lo Quiero
          </button>
        </div>
      </AnimatedSection>

      {/* WALL OF LOVE - TESTIMONIOS VERIFICABLES */}
      <WallOfLove />

      {/* FAQ - COMPACTO */}
      <AnimatedSection id="faq" className="py-24 px-6 bg-white">
        <FAQ />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          onViewportEnter={() => trackSectionView('FAQ', 'faq')}
          className="text-center mt-12"
        >
          <button 
            onClick={() => {
              trackCTAClick('cta-faq', 'Lo Quiero', 'faq');
              setIsActivationModalOpen(true);
            }}
            className={`${modernButtons.primary}`}
          >
            Lo Quiero
          </button>
        </motion.div>
      </AnimatedSection>

      {/* CTA FINAL - URGENCIA */}
      <AnimatedSection id="contacto" className="py-24 px-6 bg-gradient-to-br from-[#2A1E42] via-[#1a1530] to-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <Zap className="w-16 h-16 text-[#00C2FF] mx-auto mb-6" />
          <h2 className={`text-4xl md:text-6xl ${fontWeights.black} text-white mb-6`}>
            Actúas HOY o Sigues Perdiendo Dinero MAÑANA
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Cada día sin este sistema = $25-$100 USD directos a la basura. Tu competencia ya está cerrando las ventas que debieron ser tuyas.
          </p>
          
          <div className="bg-gradient-to-r from-[#BC9C71]/20 to-[#D4AF37]/20 border-2 border-[#BC9C71] rounded-xl p-6 mb-8 max-w-2xl mx-auto backdrop-blur-sm">
            <p className="text-[#D4AF37] text-sm font-semibold mb-2">🔥 CUPÓN REQUERIDO PARA OFERTA</p>
            <p className="text-white text-2xl font-bold mb-1">50% OFF con cupón de lanzamiento</p>
            <p className="text-gray-200 text-sm">Obtén tu cupón mientras navegas para acceder al precio promocional</p>
          </div>

          <button 
            onClick={() => {
              trackCTAClick('cta-final', 'Lo Quiero', 'contacto');
              setIsActivationModalOpen(true);
            }}
            className="px-12 py-5 rounded-full bg-gradient-to-r from-[#00C2FF] to-[#00A8E8] text-white text-xl font-bold hover:scale-105 transition-all shadow-[0_6px_0_rgba(0,168,232,0.8),0_10px_25px_rgba(0,194,255,0.4)] hover:shadow-[0_8px_0_rgba(0,168,232,0.8),0_15px_35px_rgba(0,194,255,0.5)]"
          >
            Lo Quiero
          </button>
        </div>
      </AnimatedSection>

      {/* FOOTER LIMPIO */}
      <footer className="bg-black/40 backdrop-blur-sm text-gray-400 py-12 border-t-2 border-orange-400/30">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className={`text-3xl ${fontWeights.bold} text-white mb-4`}>ZoeFel IA™</div>
          <p className="mb-6">&copy; {new Date().getFullYear()} ZoeFel IA™. Todos los derechos reservados.</p>
          
          <div className="flex justify-center gap-6 mb-6">
            <Link to="/terminos" className="hover:text-white transition-colors underline">
              Términos y Condiciones
            </Link>
            <span>|</span>
            <Link to="/privacidad" className="hover:text-white transition-colors underline">
              Política de Privacidad
            </Link>
          </div>

          <p className="text-xs text-gray-500 max-w-2xl mx-auto">
            Todos los precios en USD. Los servicios están sujetos a términos y condiciones. 
            Contacto: legal@zoepelai.com
          </p>
        </div>
      </footer>
      
      {/* Aceleradores de Conversión */}
      <SocialProof />
      <TrialPopupFlow onActivate={(planType) => handlePlanActivation(planType)} />
      
      {/* Modal de Activación */}
      <ActivationModal 
        isOpen={isActivationModalOpen} 
        onClose={() => {
          setIsActivationModalOpen(false);
          setSelectedPlan(undefined);
        }}
        preSelectedPlan={selectedPlan}
      />
    </div>
  );
}

export default App;
